import { useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/api";
import { pick, useLang } from "@/i18n";

const EMPTY = {
    date: "", time: "", passengers: 2, luggage: 0, pickup: "", dropoff: "",
    requested_stop: "", notes: "", customer_name: "", customer_email: "", customer_phone: "", privacy_consent: false,
};

export const BookingDialog = ({ pkg, open, onOpenChange }) => {
    const { lang, t } = useLang();
    const [form, setForm] = useState(EMPTY);
    const [sending, setSending] = useState(null); // 'manual' | 'instant' | null
    const [result, setResult] = useState(null);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const basePayload = () => ({
        package_id: pkg.id,
        date: form.date,
        time: form.time,
        passengers: Number(form.passengers),
        luggage: Number(form.luggage),
        pickup: form.pickup,
        dropoff: form.dropoff,
        notes: form.notes || null,
        language: lang,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        privacy_consent: form.privacy_consent,
    });

    const submitManual = async () => {
        setSending("manual");
        try {
            const payload = { ...basePayload(), requested_stop: form.requested_stop || null, origin_url: window.location.origin };
            const { data } = await api.post("/public/bookings", payload);
            setResult({ code: data.code, status: data.status });
        } catch (e) {
            toast.error(apiError(e, t.booking.errorGeneric));
        } finally {
            setSending(null);
        }
    };

    const submitInstant = async () => {
        setSending("instant");
        try {
            const payload = { ...basePayload(), origin_url: window.location.origin };
            const { data } = await api.post("/public/bookings/instant", payload);
            window.location.href = data.checkout_url;
        } catch (e) {
            const status = e?.response?.status;
            if (status === 409) {
                toast.warning(t.booking.fallbackToast);
            } else {
                toast.error(apiError(e, t.booking.errorGeneric));
            }
            setSending(null);
        }
    };

    const valid = form.date && form.time && form.customer_name.length >= 2 && form.customer_email.includes("@") && form.customer_phone.length >= 5 && form.privacy_consent;

    const close = (v) => {
        onOpenChange(v);
        if (!v) { setResult(null); setForm(EMPTY); }
    };

    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogContent data-testid="booking-dialog" className="max-w-lg bg-[#121216] border-amber-500/25 text-stone-200 max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl text-amber-50">{t.booking.title}</DialogTitle>
                    <p className="text-sm text-stone-400">{pick(pkg?.name, lang)}</p>
                </DialogHeader>

                {result ? (
                    <div className="py-8 text-center" data-testid="booking-success">
                        <CheckCircle2 className="mx-auto text-amber-400" size={52} />
                        <h3 className="mt-4 font-display text-2xl text-amber-50">{t.booking.successTitle}</h3>
                        <p className="mt-3 text-sm text-stone-300 leading-relaxed">{t.booking.successManual}</p>
                        {result.code && (
                            <div className="mt-6 inline-block rounded-lg border border-amber-500/40 bg-black/50 px-6 py-3">
                                <p className="text-[10px] uppercase tracking-[0.3em] text-amber-500/80">{t.booking.bookingCode}</p>
                                <p data-testid="booking-success-code" className="font-mono-accent text-2xl text-amber-300 mt-1">{result.code}</p>
                                <p className="mt-1 text-xs text-stone-400">{t.status[result.status] || result.status}</p>
                            </div>
                        )}
                        <div className="mt-6 flex flex-col items-center gap-3">
                            <Link to={`/stato-prenotazione?code=${result.code || ""}`} data-testid="booking-status-link" className="text-xs uppercase tracking-[0.14em] text-amber-300 underline underline-offset-4 hover:text-amber-200">
                                {t.nav.statusPage}
                            </Link>
                            <button data-testid="booking-close-button" onClick={() => close(false)} className="rounded-full border border-amber-500/40 px-6 py-2 text-xs uppercase tracking-widest text-amber-200 hover:bg-amber-400 hover:text-black transition-colors">
                                {t.booking.close}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-3 pt-2">
                        <label className="block">
                            <span className="text-[11px] uppercase tracking-widest text-amber-500/80">{t.booking.date} *</span>
                            <input data-testid="booking-date-input" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="input-lux mt-1" />
                        </label>
                        <label className="block">
                            <span className="text-[11px] uppercase tracking-widest text-amber-500/80">{t.booking.time} *</span>
                            <input data-testid="booking-time-input" type="time" value={form.time} onChange={(e) => set("time", e.target.value)} className="input-lux mt-1" />
                        </label>
                        <label className="block">
                            <span className="text-[11px] uppercase tracking-widest text-amber-500/80">{t.booking.passengers} *</span>
                            <input data-testid="booking-passengers-input" type="number" min="1" max={pkg?.max_passengers || 16} value={form.passengers} onChange={(e) => set("passengers", e.target.value)} className="input-lux mt-1" />
                        </label>
                        <label className="block">
                            <span className="text-[11px] uppercase tracking-widest text-amber-500/80">{t.booking.luggage}</span>
                            <input data-testid="booking-luggage-input" type="number" min="0" max="20" value={form.luggage} onChange={(e) => set("luggage", e.target.value)} className="input-lux mt-1" />
                        </label>
                        <label className="block col-span-2 sm:col-span-1">
                            <span className="text-[11px] uppercase tracking-widest text-amber-500/80">{t.booking.pickup}</span>
                            <input data-testid="booking-pickup-input" type="text" value={form.pickup} onChange={(e) => set("pickup", e.target.value)} className="input-lux mt-1" />
                        </label>
                        <label className="block col-span-2 sm:col-span-1">
                            <span className="text-[11px] uppercase tracking-widest text-amber-500/80">{t.booking.dropoff}</span>
                            <input data-testid="booking-dropoff-input" type="text" value={form.dropoff} onChange={(e) => set("dropoff", e.target.value)} className="input-lux mt-1" />
                        </label>
                        {pkg?.optional_stop_allowed && (
                            <label className="block col-span-2">
                                <span className="text-[11px] uppercase tracking-widest text-amber-500/80">{t.booking.stopLabel}</span>
                                <input data-testid="booking-stop-input" type="text" value={form.requested_stop} onChange={(e) => set("requested_stop", e.target.value)} className="input-lux mt-1" />
                            </label>
                        )}
                        <label className="block col-span-2">
                            <span className="text-[11px] uppercase tracking-widest text-amber-500/80">{t.booking.notes}</span>
                            <textarea data-testid="booking-notes-input" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="input-lux mt-1" />
                        </label>
                        <label className="block col-span-2">
                            <span className="text-[11px] uppercase tracking-widest text-amber-500/80">{t.booking.name} *</span>
                            <input data-testid="booking-name-input" type="text" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} className="input-lux mt-1" />
                        </label>
                        <label className="block col-span-2 sm:col-span-1">
                            <span className="text-[11px] uppercase tracking-widest text-amber-500/80">{t.booking.email} *</span>
                            <input data-testid="booking-email-input" type="email" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} className="input-lux mt-1" />
                        </label>
                        <label className="block col-span-2 sm:col-span-1">
                            <span className="text-[11px] uppercase tracking-widest text-amber-500/80">{t.booking.phone} *</span>
                            <input data-testid="booking-phone-input" type="tel" value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} className="input-lux mt-1" />
                        </label>
                        <label className="col-span-2 flex items-start gap-3 text-xs text-stone-400 cursor-pointer">
                            <input data-testid="booking-privacy-checkbox" type="checkbox" checked={form.privacy_consent} onChange={(e) => set("privacy_consent", e.target.checked)} className="mt-0.5 accent-amber-400" />
                            {t.booking.privacy}
                        </label>
                        <div className="col-span-2 flex flex-col sm:flex-row gap-3 pt-2">
                            <button
                                data-testid="booking-submit-request"
                                disabled={!valid || sending !== null}
                                onClick={submitManual}
                                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-amber-500/50 px-5 py-3 text-xs font-bold uppercase tracking-[0.12em] text-amber-200 transition-colors hover:bg-amber-400/10 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {sending === "manual" && <Loader2 size={14} className="animate-spin" />}
                                {t.booking.submitRequest}
                            </button>
                            {pkg?.allow_instant_booking && pkg?.price > 0 && (
                                <button
                                    data-testid="booking-submit-instant"
                                    disabled={!valid || sending !== null}
                                    onClick={submitInstant}
                                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 px-5 py-3 text-xs font-bold uppercase tracking-[0.12em] text-black transition-all hover:bg-amber-300 hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    {sending === "instant" && <Loader2 size={14} className="animate-spin" />}
                                    {t.booking.submitInstant} · €{pkg.price.toFixed(2)}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
